import { Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  DataLicense,
  FileResource,
  Submission,
  SubmissionControllerService
} from "../../../../modules/lipidcompass-backend-client";
import { submissionWorkflowConfig } from "../../../environments/environment";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { NotificationService } from "../../_services/notification.service";
import {
  manifestOf,
  roleOf,
  siblingsOf
} from "../../_services/submission-datasets.util";
import { ConfirmationService } from "primeng/api";
import { ConfirmDialogModule } from "primeng/confirmdialog";

import { CardModule } from "primeng/card";
import { SelectModule } from "primeng/select";
import { ButtonGroupModule } from "primeng/buttongroup";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { TableModule } from "primeng/table";
import { TranslateModule } from "@ngx-translate/core";
import { FormsModule } from "@angular/forms";

/**
 * Scopes this component's confirmations to its own dialog. See {@link
 * UploadComponent#deleteFile} for why a key is required here specifically.
 */
export const UPLOAD_DELETE_CONFIRM_KEY = "uploadDeleteFile";

@Component({
  selector: "app-upload",
  templateUrl: "./upload.component.html",
  styleUrls: ["./upload.component.scss"],
  standalone: true,
  imports: [
    ButtonModule,
    ButtonGroupModule,
    CardModule,
    CommonModule,
    ConfirmDialogModule,
    SelectModule,
    FormsModule,
    TableModule,
    TranslateModule
  ]
})
export class UploadComponent implements OnInit {
  private submissionService = inject(SubmissionControllerService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private loggingService = inject(LoggingService);
  private confirmationService = inject(ConfirmationService);

  submissionId: string | undefined;
  activeIndex: number = 2;
  submission: Submission | undefined;
  dataLicenses: DataLicense[] | undefined;
  dataLicense: DataLicense | undefined;
  file: File | undefined = undefined;
  public progress: boolean = false;

  ngOnInit() {
    this.submissionId = this.route.snapshot.params.id;
    if (this.submissionId) {
      this.submissionService.submissionGetById(this.submissionId).subscribe(result => {
        this.submission = <Submission>result;
        if (this.submission?.study?.dataLicense) {
          this.dataLicense = this.submission.study.dataLicense;
        }
      });
      this.submissionService.submissionGetDataLicenses().subscribe(result => {
        this.dataLicenses = result?._embedded?.dataLicenses || [];
      });
    }
  }

  clear() {
    this.file = undefined;
  }

  onSelectFile(event: any) {
    this.loggingService.log(
      "UploadComponent",
      "onSelectFile: " + JSON.stringify(event),
      LogLevel.DEBUG
    );
    const file: File = event.target.files[0];
    if (file) {
      this.loggingService.log(
        "UploadComponent",
        "Setting file: " + JSON.stringify(file.name),
        LogLevel.DEBUG
      );
      this.file = file;
    }
  }

  /**
   * The rows of the "Files in submission" manifest: one per dataset, not one
   * per stored artifact.
   *
   * Unlike the summary and dashboard manifests, every row here carries a
   * Delete button and no download action. Deletion cascades over the whole
   * dataset server-side, so listing the submitter's ORIGINAL and the platform's
   * derived WORKING copy as two rows would offer two buttons that each destroy
   * both files -- a file the submitter never uploaded, presented as
   * independently deletable.
   */
  submittedFileRows(): FileResource[] {
    return manifestOf(this.submission?.submittedFiles);
  }

  /**
   * What accepting the delete will actually remove.
   *
   * Deliberately never says "this file". The row is labelled with the SERVABLE
   * artifact, which for a stamped dataset is the derived working copy
   * (LCUS-00000042-01.mzTab) -- a name the submitter has never seen. Naming the
   * dataset, and naming the original upload that goes with it, is the whole
   * point of asking.
   */
  deleteConfirmationMessage(file: FileResource): string {
    const artifacts = siblingsOf(this.submission?.submittedFiles, file);
    const derived = artifacts.filter(
      f => roleOf(f) !== FileResource.RoleEnum.ORIGINAL
    );
    if (derived.length === 0) {
      // A plain file, or a legacy mzTab with nothing derived from it.
      return (
        `Delete ${file.fileName} from this submission? ` +
        `This cannot be undone.`
      );
    }
    const original = artifacts.find(
      f => roleOf(f) === FileResource.RoleEnum.ORIGINAL
    );
    const datasetLabel = file.datasetIndex
      ? `dataset ${file.datasetIndex}`
      : "this dataset";
    return (
      `Delete ${datasetLabel} from this submission? This removes all ` +
      `${artifacts.length} files of the dataset: the file you uploaded ` +
      `(${original?.fileName ?? "unknown"}) and the copy LipidCompass derived ` +
      `from it (${derived.map(f => f.fileName).join(", ")}). ` +
      `This cannot be undone.`
    );
  }

  deleteFile(file: FileResource) {
    this.confirmationService.confirm({
      // Keyed, unlike every other confirm dialog in this codebase, and it must
      // be. UploadComponent renders inside DataSubmitComponent's router-outlet,
      // and that parent already has an UNKEYED <p-confirmDialog>. PrimeNG
      // broadcasts each confirmation to every mounted dialog and accepts it
      // when `confirmation.key === this.key`, so an unkeyed dialog here would
      // match the parent's too and open two stacked dialogs.
      key: UPLOAD_DELETE_CONFIRM_KEY,
      message: this.deleteConfirmationMessage(file),
      accept: () => this.performDeleteFile(file)
    });
  }

  private performDeleteFile(file: FileResource) {
    this.loggingService.log(
      "UploadComponent",
      "deleteFile: " + JSON.stringify(file),
      LogLevel.DEBUG
    );
    if (this.submissionId) {
      this.submissionService
        .submissionDeleteFile(this.submissionId, file)
        .subscribe(result => {
          this.loggingService.log(
            "UploadComponent",
            "Received response: " + JSON.stringify(result),
            LogLevel.DEBUG
          );
          this.submission = result;
        });
    }
  }

  myCustomUpload(event: any) {
    this.loggingService.log(
      "UploadComponent",
      "myCustomUpload: " + JSON.stringify(event),
      LogLevel.DEBUG
    );
    if (this.file) {
      this.loggingService.log(
        "UploadComponent",
        "File is populated",
        LogLevel.DEBUG
      );
      this.progress = true;
      this.loggingService.log(
        "UploadComponent",
        "Using data license: " + JSON.stringify(this.dataLicense),
        LogLevel.DEBUG
      );
      if (this.submissionId && this.dataLicense) {
        this.submissionService
          .submissionUpload(this.submissionId, this.dataLicense, this.file)
          .subscribe(
            response => {
              this.loggingService.log(
                "UploadComponent",
                "Received response: " + JSON.stringify(response),
                LogLevel.DEBUG
              );
              this.submission = response;
              this.file = undefined;
              this.dataLicense = undefined;
              this.progress = false;
            },
            error => {
              this.loggingService.log(
                "UploadComponent",
                "Received error response: " + JSON.stringify(error),
                LogLevel.DEBUG
              );
              this.progress = false;
              this.notificationService.publish(
                "messages",
                "error",
                "Error",
                JSON.stringify(error)
              );
            }
          );
      } else {
        this.loggingService.log(
          "UploadComponent",
          "No submission ID or data licensefound",
          LogLevel.DEBUG
        );
      }
    } else {
      this.loggingService.log(
        "UploadComponent",
        "FileForm is not populated",
        LogLevel.DEBUG
      );
    }
    return;
  }

  isFormValid(): boolean {
    var inputOk = this.isFileSelectionValid() && this.isLicenseValid();
    return inputOk; // && !this.fileExists();
  }

  isFileSelectionValid(): boolean {
    return this.file != null;
  }

  isLicenseValid(): boolean {
    this.loggingService.log(
      "UploadComponent",
      "isLicenseValid: " + JSON.stringify(this.dataLicense),
      LogLevel.DEBUG
    );
    return this.dataLicense != null;
  }

  fileExists(): boolean {
    return (
      this.submission?.submittedFiles?.some(
        value => value.fileName === this.file?.name
      ) || false
    );
  }

  getFormInputValidClasses() {
    if (this.isFormValid()) {
      return {};
    } else {
      return {
        "ng-invalid": true,
        "ng-dirty": true
      };
    }
  }

  hasMzTabFiles(): boolean {
    return (
      this.submission?.submittedFiles?.some(
        value => value.fileType === "MZTAB_M"
      ) || false
    );
  }

  nextPage(event: any) {
    this.loggingService.log(
      "UploadComponent",
      "nextPage: " + JSON.stringify(event),
      LogLevel.DEBUG
    );
    this.router.navigate(
      [
        submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex]
          .routerLink,
        this.submissionId
      ],
      { relativeTo: this.route.parent }
    );
    return;
  }

  previousPage(event: any) {
    this.loggingService.log(
      "UploadComponent",
      "previousPage: " + JSON.stringify(event),
      LogLevel.DEBUG
    );
    if (this.activeIndex > 1) {
      this.loggingService.log(
        "UploadComponent",
        "previousPage via activeIndex: " +
          JSON.stringify([
            submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex - 2]
              .routerLink,
            this.submissionId
          ]),
        LogLevel.DEBUG
      );
      this.loggingService.log(
        "UploadComponent",
        "Active index: " +
          (this.activeIndex - 2) +
          " is " +
          JSON.stringify(
            submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex - 2]
              .routerLink
          ),
        LogLevel.DEBUG
      );
      this.router.navigate(
        [
          submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex - 2]
            .routerLink,
          this.submissionId
        ],
        { relativeTo: this.route.parent }
      );
    } else {
      this.loggingService.log(
        "UploadComponent",
        "previousPage to parent: " + JSON.stringify([this.route.parent]),
        LogLevel.DEBUG
      );
      this.router.navigate(submissionWorkflowConfig.parentRoute);
    }
    return;
  }

  cancel(event: any) {
    this.router.navigate(submissionWorkflowConfig.parentRoute);
    return;
  }
}
